import React, { forwardRef, useImperativeHandle, useState } from "react";

export type CarouselHandle = {
	next: () => void;
	prev: () => void;
	goTo: (index: number) => void;
};

type CarouselProps = {
	onChange?: ({ current, count }: { current: number; count: number }) => void;
	slides: React.ReactNode[];
	className?: string;
	children?:
		| (({
				next,
				prev,
				count,
				current,
		  }: {
				count: number;
				current: number;
				next: () => void;
				prev: () => void;
		  }) => React.ReactNode)
		| React.ReactNode;
};

const Carousel = forwardRef<CarouselHandle, CarouselProps>(function Carousel(
	{ slides, children, onChange, className },
	ref
) {
	const [current, setCurrent] = useState(0);

	const goTo = (index: number) => {
		setCurrent(index);
		onChange?.({ current: index, count: slides.length });
	};

	const prev = () => {
		const newIndex = current === 0 ? slides.length - 1 : current - 1;
		setCurrent(newIndex);
		onChange?.({ current: newIndex, count: slides.length });
	};

	const next = () => {
		const newIndex = current === slides.length - 1 ? 0 : current + 1;
		setCurrent(newIndex);
		onChange?.({ current: newIndex, count: slides.length });
	};

	useImperativeHandle(
		ref,
		() => ({
			next,
			prev,
			goTo,
		}),
		[current, slides.length, onChange, goTo, next, prev]
	);

	if (!slides.length) return null;

	return (
		<div
			className={`mx-auto flex h-full min-h-0 w-full flex-col ${className}`}
		>
			<div className="relative min-h-0 flex-1 overflow-hidden">
				<div
					className="flex h-full transition-transform duration-300 ease-out"
					style={{ transform: `translateX(-${current * 100}%)` }}
				>
					{slides.map((slide, i) => (
						<div key={i} className="h-full w-full shrink-0">
							{slide}
						</div>
					))}
				</div>
			</div>

			<div className="flex justify-center gap-2 pt-4 sm:gap-4">
				{slides.map((_, i) => (
					<button
						key={i}
						onClick={(e) => {
							e.preventDefault();
							goTo(i);
						}}
						aria-label={`Go to slide ${i + 1}`}
						className={`h-4 w-4 rounded-full ${
							i === current
								? "bg-grey-dark"
								: "bg-grey-light cursor-pointer"
						}`}
					/>
				))}
			</div>
			{children && (
				<div className="mt-8">
					{typeof children === "function"
						? children({
								next,
								prev,
								count: slides.length,
								current,
							})
						: children}
				</div>
			)}
		</div>
	);
});

export default Carousel;
